import {useCallback, useEffect, useMemo, useReducer, useState} from 'react';
import {isEqual} from 'lodash';

import {AggregatedFact} from '../../../../../api/kpiApi';

import {fetchAggregatedFacts, fetchKpiValue} from '../kpiUtils';
import {AggregationSettings} from '../types';
import {useUserAccountInfo, WidgetProps} from '@deviceinsight/ng-ui-scale-lib/api';
import makeCancelable from '../../../../../utils/makeCancelable';
import {useMultiTelemetry} from 'ux4iot-react';
import {usePreviousValue} from '../../../../../hooks/usePreviousValue';

export type KpiIdentifier = {
	assetUrn: string;
	kpiName: string;
};
export type KpiValueDescriptor = KpiIdentifier & {
	value?: number | null | undefined;
	facts?: AggregatedFact[] | null | undefined;
};
type KpiDescriptor = KpiValueDescriptor & {
	shouldBeUpdated: boolean;
};
type InitializeAction = {
	type: 'initialize';
	payload: KpiIdentifier[];
};
type RequestFetchingAction = {
	type: 'requestFetching';
	payload: KpiIdentifier;
};
type CompleteFetchingAction = {
	type: 'completeFetching';
	payload: KpiValueDescriptor[];
};
type UpdateAllAction = {
	type: 'updateAll';
};
type Action = InitializeAction | CompleteFetchingAction | RequestFetchingAction | UpdateAllAction;

const comparator = (item1: Partial<KpiIdentifier>) => (item2: Partial<KpiIdentifier>) =>
	item1.assetUrn === item2.assetUrn && item1.kpiName === item2.kpiName;

type MultipleKpiData = {
	values: KpiValueDescriptor[];
	fetching: boolean;
	errorMessage: string | null | undefined;
	lastUpdate: number | null | undefined;
	reload: () => void;
};
export default (
	context: WidgetProps['context'],
	settings: AggregationSettings,
	widgetKey: string,
	forcedTime: number | null | undefined,
	editMode: boolean,
	aggregatedFacts?: boolean // if true, aggregated facts will be fetched instead of values
): MultipleKpiData => {
	const items = useMemo(
		() =>
			(settings.items ?? []).map((item) => ({
				...item,
				assetUrn: context.type === 'asset' ? context.asset.urn : item.assetUrn
			})),
		[context, settings.items]
	);
	const user = useUserAccountInfo();
	const userId = user.id;
	const [errorMessage, setErrorMessage] = useState<string | null | undefined>(null);
	const [kpiIdentifiers, setKpiIdentifiers] = useState<KpiIdentifier[]>(items);
	const [fetching, setFetching] = useState<boolean>(false);
	const [lastUpdate, setLastUpdate] = useState<number | null | undefined>(null);
	const [reloadTrigger, setReloadTrigger] = useState<number>(0);
	const reload = useCallback(() => setReloadTrigger((trigger) => trigger + 1), []);
	useEffect(() => {
		if (!isEqual(kpiIdentifiers, items)) {
			setKpiIdentifiers(items);
		}
	}, [items, kpiIdentifiers]);
	const [descriptors, dispatch] = useReducer((descriptors: KpiDescriptor[], action: Action) => {
		if (editMode) {
			return descriptors;
		}

		switch (action.type) {
			case 'initialize':
				return action.payload.map(
					(kpiIdentifier) =>
						descriptors.find(comparator(kpiIdentifier)) || {
							kpiName: kpiIdentifier.kpiName,
							assetUrn: kpiIdentifier.assetUrn,
							value: null,
							facts: [],
							shouldBeUpdated: true
						}
				);

			case 'requestFetching':
				return descriptors.map((descriptor) =>
					comparator(descriptor)(action.payload) ? {...descriptor, shouldBeUpdated: true} : descriptor
				);

			case 'completeFetching':
				return descriptors.map((descriptor) => {
					const updatedItem = action.payload.find(comparator(descriptor));
					return updatedItem
						? {...descriptor, shouldBeUpdated: false, value: updatedItem.value, facts: updatedItem.facts}
						: descriptor;
				});

			case 'updateAll':
				return descriptors.map((descriptor) => ({...descriptor, shouldBeUpdated: true}));

			default:
				return descriptors;
		}
	}, []);
	useEffect(() => {
		dispatch({
			type: 'initialize',
			payload: kpiIdentifiers
		});
	}, [kpiIdentifiers]);
	useEffect(() => {
		dispatch({
			type: 'updateAll'
		});
	}, [forcedTime, reloadTrigger]);
	useEffect(() => {
		const itemsToUpdate = descriptors
			.filter(({shouldBeUpdated}) => shouldBeUpdated)
			.map(({kpiName, assetUrn}) => ({
				kpiName,
				assetUrn
			}));
		setFetching(!!itemsToUpdate.length);

		if (itemsToUpdate.length) {
			setErrorMessage(null);
			const time = forcedTime || Date.now();
			const promises = itemsToUpdate.map((identifier) => {
				return aggregatedFacts
					? fetchAggregatedFacts(identifier, userId, time, settings)
					: fetchKpiValue(identifier, userId, time, settings);
			});
			const {promise, cancel} = makeCancelable(Promise.all(promises).catch((e) => setErrorMessage(e.message)));
			promise
				.then((updatedItems) => {
					if (updatedItems)
						dispatch({
							type: 'completeFetching',
							payload: updatedItems
						});
					setLastUpdate(time);
				})
				.catch((e) => {
					if (!e.isCanceled) console.error(e);
				});
			return cancel;
		}
	}, [descriptors, forcedTime, userId, settings, aggregatedFacts]);

	const {addTelemetry, removeTelemetry} = useMultiTelemetry({
		onData: (assetUrn, value) => {
			if (value?.type === 'kpiFactStored') {
				const matchingIdentifier = kpiIdentifiers.find(
					(identifier) => identifier.assetUrn === assetUrn && identifier.kpiName === value.kpiName
				);
				if (matchingIdentifier) {
					dispatch({
						type: 'requestFetching',
						payload: matchingIdentifier
					});
				}
			}
		}
	});

	const prevKpiIdentifiers = usePreviousValue(kpiIdentifiers);

	useEffect(() => {
		const removedIdentifiers = prevKpiIdentifiers?.filter(
			(prevIdentifier) => !kpiIdentifiers.some((identifier) => identifier.assetUrn === prevIdentifier.assetUrn)
		);

		if (removedIdentifiers?.length) {
			const assetUrnsToRemove = [...new Set(removedIdentifiers.map((id) => id.assetUrn))];
			assetUrnsToRemove.forEach((assetUrn) => {
				removeTelemetry(assetUrn, ['kpiFactStored']);
			});
		}

		const addedIdentifiers = kpiIdentifiers.filter(
			(identifier) =>
				!prevKpiIdentifiers?.some((prevIdentifier) => prevIdentifier.assetUrn === identifier.assetUrn)
		);

		if (addedIdentifiers?.length) {
			const assetUrnsToAdd = [...new Set(addedIdentifiers.map((id) => id.assetUrn))];
			assetUrnsToAdd.forEach((assetUrn) => {
				addTelemetry(assetUrn, ['kpiFactStored']);
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [kpiIdentifiers]);

	useEffect(() => {
		kpiIdentifiers.forEach((identifier) => {
			dispatch({
				type: 'requestFetching',
				payload: identifier
			});
		});
	}, [settings.query.periodType, settings.query.customPeriodFrom, settings.query.customPeriodTo, kpiIdentifiers]);

	const values = useMemo(
		() =>
			descriptors.map(({kpiName, assetUrn, value, facts}) => ({
				kpiName,
				assetUrn,
				value,
				facts
			})),
		[descriptors]
	);
	return {
		values,
		fetching,
		errorMessage,
		lastUpdate,
		reload
	};
};
